import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Brain, Sparkles, User as UserIcon, Wand2, Search, Settings as SettingsIcon } from "lucide-react";

const AI_MODES = [
  {
    value: "default",
    icon: Sparkles,
    label: "Default persona",
    desc: "Štandardná flirty persona z /ai nastavení.",
  },
  {
    value: "mirror",
    icon: Wand2,
    label: "Mirror môj štýl",
    desc: "AI imituje TVOJ štýl písania — slang, dĺžku správ, emojis. Učí sa z toho ako si písal/a pred ním.",
  },
  {
    value: "discovery",
    icon: Search,
    label: "Discovery mode",
    desc: "AI sa v každej odpovedi pýta jednu otázku — postupne objavuje fakty o ňom (job, mesto, kinks).",
  },
  {
    value: "custom",
    icon: SettingsIcon,
    label: "Custom inštrukcie",
    desc: "Napíš AI vlastné inštrukcie len pre tohto fana. Override všetkého.",
  },
];

export default function FanProfile({ fan, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiMode, setAiMode] = useState(fan?.ai_mode || "default");
  const [customInstr, setCustomInstr] = useState(fan?.custom_instructions || "");
  const [personaNotes, setPersonaNotes] = useState(fan?.persona_notes || "");
  const [ppvMode, setPpvMode] = useState(Number(fan?.ppv_mode ?? 0));

  const openDialog = () => {
    setAiMode(fan?.ai_mode || "default");
    setPpvMode(Number(fan?.ppv_mode ?? 0));
    setCustomInstr(fan?.custom_instructions || "");
    setPersonaNotes(fan?.persona_notes || "");
    setOpen(true);
  };

  const generateDossier = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/ai/regenerate-notes/${fan.id}`);
      if (r.data.notes) {
        setPersonaNotes(r.data.notes);
        toast.success("Dossier vygenerovaný");
      } else {
        toast.error("Žiadne správy na analýzu");
      }
    } catch (e) {
      toast.error("Generovanie zlyhalo");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/fans/${fan.id}`, {
        ai_mode: aiMode,
        custom_instructions: customInstr,
        persona_notes: personaNotes,
        ppv_mode: ppvMode,
      });
      toast.success("Uložené");
      onUpdate?.(r.data);
      setOpen(false);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={openDialog}
        className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white border border-white/10 px-3 py-1.5 flex items-center gap-1.5"
        data-testid="btn-open-fan-profile"
        title="Fan dossier + AI mód"
      >
        <Brain size={11} /> Profil
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[rgb(18,20,28)] border-white/10 text-white max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl flex items-center gap-3">
              <UserIcon size={22} className="text-fuchsia-400" />
              {fan?.name}
              <span className="text-[10px] uppercase tracking-widest text-white/40 font-sans">
                {fan?.platform} · ${(fan?.spend_total || 0).toFixed(2)}
              </span>
            </DialogTitle>
          </DialogHeader>

          {/* Quick facts row */}
          <div className="grid grid-cols-3 gap-3 text-xs border-y border-white/5 py-3">
            <Stat label="Status" value={fan?.fan_status || "—"} />
            <Stat label="NSFW" value={fan?.nsfw_unlocked ? "ON" : "OFF"} color={fan?.nsfw_unlocked ? "text-rose-300" : "text-white/40"} />
            <Stat label="AI" value={fan?.ai_enabled !== false ? "ON" : "OFF"} color={fan?.ai_enabled !== false ? "text-emerald-300" : "text-white/40"} />
          </div>

          {/* AI mode picker */}
          <div>
            <Label className="text-base font-serif">AI mód pre tohto fana</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
              {AI_MODES.map((m) => {
                const Icon = m.icon;
                const active = aiMode === m.value;
                return (
                  <button
                    key={m.value}
                    onClick={() => setAiMode(m.value)}
                    data-testid={`ai-mode-${m.value}`}
                    className={`text-left p-3 border transition-all ${
                      active
                        ? "border-fuchsia-500 bg-fuchsia-500/10"
                        : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={14} className={active ? "text-fuchsia-300" : "text-white/50"} />
                      <span className={`text-xs uppercase tracking-widest font-medium ${active ? "text-fuchsia-200" : "text-white/80"}`}>
                        {m.label}
                      </span>
                    </div>
                    <div className="text-[11px] text-white/55 leading-relaxed">{m.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom instructions (only if custom mode) */}
          {aiMode === "custom" && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-white/40">
                Custom inštrukcie pre tohto fana
              </Label>
              <Textarea
                value={customInstr}
                onChange={(e) => setCustomInstr(e.target.value)}
                rows={4}
                placeholder='napr. "Vie že mám psa Reksa. Volá ma princezná. Píš krátko, nepoužívaj viac ako 1 emoji. Nikdy nespomínaj jeho exku Janu."'
                className="bg-transparent border-white/10 rounded-none mt-1 text-sm"
                data-testid="textarea-custom-instructions"
              />
            </div>
          )}

          {/* ── PPV mód pre tohto fana ── */}
          <div className="mt-6">
            <div className="text-sm font-bold text-white mb-2">PPV mód pre tohto fana</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 0, l: "Podľa nastavení", d: "Riadi sa globálnymi AI PPV pravidlami." },
                { v: 1, l: "Aktívne ponuky", d: "3× kratší cooldown, bez čakania na prvú fotku." },
                { v: 2, l: "Explicit", d: "Preferuje explicit fotky, bez NSFW prahu (vždy platené)." },
              ].map((o) => (
                <button
                  key={o.v}
                  onClick={() => setPpvMode(o.v)}
                  data-testid={`ppvmode-${o.v}`}
                  className={`text-left border p-3 transition-colors ${
                    ppvMode === o.v
                      ? "border-fuchsia-500 bg-fuchsia-500/10"
                      : "border-white/10 hover:border-white/25"
                  }`}
                >
                  <div className={`text-[11px] uppercase tracking-widest font-bold ${ppvMode === o.v ? "text-fuchsia-300" : "text-white/70"}`}>{o.l}</div>
                  <div className="text-[10px] text-white/40 mt-1 leading-snug">{o.d}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Fan dossier */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base font-serif">Dossier / výcuc faktov</Label>
              <Button
                onClick={generateDossier}
                disabled={generating}
                data-testid="btn-generate-dossier"
                className="bg-fuchsia-500/20 border border-fuchsia-500/30 text-fuchsia-200 hover:bg-fuchsia-500/30 rounded-none text-[10px] uppercase tracking-widest h-8"
              >
                <Brain size={11} className="mr-1.5" />
                {generating ? "Analyzujem…" : personaNotes ? "Regenerovať" : "Vygenerovať z chatu"}
              </Button>
            </div>
            <div className="text-[11px] text-white/40 mb-2">
              AI prečíta posledných 50 správ a vytiahne fakty: meno, mesto, záľuby, NSFW záujem,
              komunikačný štýl, hooks na ďalšie správy.
            </div>
            <Textarea
              value={personaNotes}
              onChange={(e) => setPersonaNotes(e.target.value)}
              rows={12}
              placeholder="Žiadny dossier zatiaľ. Klikni Vygenerovať alebo napíš ručne."
              className="bg-transparent border-white/10 rounded-none text-xs font-mono leading-relaxed"
              data-testid="textarea-persona-notes"
            />
            <div className="text-[10px] text-white/30 mt-1">
              Tip: tieto fakty AI <b>automaticky používa</b> pri každej odpovedi (vďaka tomu si pamätá jeho meno, mesto, kinks).
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
            <button onClick={() => setOpen(false)} className="text-xs uppercase tracking-widest text-white/40 hover:text-white">
              Zrušiť
            </button>
            <Button
              onClick={save}
              disabled={busy}
              data-testid="btn-save-fan-profile"
              className="bg-white text-black hover:bg-white/90 rounded-none text-xs uppercase tracking-widest px-6 py-5"
            >
              {busy ? "..." : "Uložiť"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, value, color = "text-white" }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`font-serif text-sm mt-1 ${color}`}>{value}</div>
    </div>
  );
}

          
          
